import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Copy, ShareNetwork, Printer, Globe, Check } from "@phosphor-icons/react";

/**
 * PublicOrderLinkCard — shows the shareable customer-facing ordering
 * URL that guests use to place pickup / delivery / QR-code orders.
 * Renders a big QR code, a copy button, a share sheet (mobile), and
 * a "Print poster" fallback for the countertop.
 *
 * The URL is derived from ``window.location.origin`` so it works
 * unchanged for both preview + production deployments.
 */
const PublicOrderLinkCard = () => {
  const orderUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/order`;
  }, []);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(orderUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the field to copy manually");
    }
  };

  const share = async () => {
    if (!navigator.share) {
      copy();
      return;
    }
    try {
      await navigator.share({
        title: "Order Online",
        text: "Order online from us",
        url: orderUrl,
      });
    } catch { /* user cancelled */ }
  };

  const printPoster = () => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) { toast.error("Popup blocked"); return; }
    w.document.write(`
      <html>
        <head><title>Order Online — QR poster</title>
        <style>
          @page { size: letter; margin: 12mm; }
          body { font-family: system-ui, -apple-system, sans-serif; text-align:center; padding: 40px 20px; }
          h1 { font-size: 42px; margin: 0 0 8px; }
          p  { font-size: 22px; color:#555; margin: 0 0 24px; }
          .qr { display:flex; justify-content:center; margin: 24px 0; }
          .url { font-family: 'Courier New', monospace; font-size: 22px; color:#333; margin-top: 16px; word-break: break-all; }
          .tag { color:#666; font-size:16px; }
        </style></head>
        <body>
          <h1>Order Online</h1>
          <p>Scan the QR or visit the link below</p>
          <div class="qr">${document.getElementById("public-order-qr")?.outerHTML || ""}</div>
          <div class="url">${orderUrl}</div>
          <p class="tag">Pickup & delivery available</p>
          <script>window.onload=()=>setTimeout(()=>{window.print();window.close();},250)</script>
        </body>
      </html>
    `);
    w.document.close();
  };

  return (
    <Card className="mb-4" data-testid="public-order-link-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Globe size={20} weight="fill" className="text-primary" />
          Public Ordering Link
        </CardTitle>
        <CardDescription>
          The single URL your customers can bookmark, scan, or install as an app to place pickup / delivery orders.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 items-start">
          <div className="bg-white border border-border rounded-xl p-3 mx-auto">
            <QRCodeSVG id="public-order-qr" value={orderUrl} size={160} includeMargin={false} />
          </div>
          <div className="space-y-3 min-w-0">
            <div className="flex items-stretch gap-2">
              <Input
                readOnly
                value={orderUrl}
                onFocus={(e) => e.target.select()}
                className="font-mono"
                data-testid="public-order-url-input"
              />
              <Button variant="outline" onClick={copy} className="gap-1 shrink-0" data-testid="copy-public-order-url">
                {copied ? <Check size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={share} className="gap-1" data-testid="share-public-order-url">
                <ShareNetwork size={16} weight="bold" /> Share
              </Button>
              <Button variant="outline" onClick={printPoster} className="gap-1" data-testid="print-public-order-poster">
                <Printer size={16} weight="bold" /> Print Poster
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: put this link in your Google Business profile, Instagram bio, and receipt footer so first-time guests can install it once and reorder in a tap.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PublicOrderLinkCard;
